// ============================================================
// PlanApplicationPage.jsx
//
// Application form for users to register their interest in
// a plan. Submits data to Firestore "applications" collection.
//
// Flow:
//   1. User fills out the form
//   2. On submit → data saved to Firestore
//   3. Success snackbar shown, form reset
//   4. Admin reviews applications and grants access
//
// Clean Code:
//   - BRAND colors centralized
//   - FORM_FIELDS array drives all text field rendering
//   - INITIAL_FORM extracted as constant for clean reset
//   - No repeated JSX — fields rendered via .map()
// ============================================================

import React, { useState } from "react";
import {
    Card, TextField, Typography, Button,
    MenuItem, Snackbar, Alert, Box,
    styled, Chip, Divider, CircularProgress,
    InputAdornment,
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────────
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import PublicIcon from "@mui/icons-material/Public";
import CakeIcon from "@mui/icons-material/Cake";
import EditNoteIcon from "@mui/icons-material/EditNote";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SchoolIcon from "@mui/icons-material/School";

// ── Firebase ─────────────────────────────────────────────────
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

// ============================================================
//  CONSTANTS
// ============================================================

/**
 * Brand colors — consistent with the rest of the platform.
 */
const BRAND = {
    dark:  "#100720",
    gold:  "#E5C95F",
    white: "#FFFFFF",
    muted: "#555",
};

/** Background image */
const BG_IMAGE = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/**
 * PLAN_OPTIONS
 * Available plans shown in the select dropdown.
 * Updated to match the actual platform plans.
 */
const PLAN_OPTIONS = [
    { value: "Basic",              label: "Basic Plan — All courses, compiler, DSA, certification" },
    { value: "Standard",           label: "Standard Plan — Basic + online coding support" },
    { value: "Premium Individual", label: "Premium Individual — Standard + project deployment" },
];

/**
 * INITIAL_FORM
 * Default empty state for the form.
 * Extracted so we can reset cleanly after submission
 * without duplicating the shape in multiple places.
 */
const INITIAL_FORM = {
    username:     "",
    firstname:    "",
    lastname:     "",
    age:          "",
    nationality:  "",
    email:        "",
    phone:        "",
    organization: "",
    plan:         "",
};

/**
 * FORM_FIELDS
 * Configuration for every text input field.
 * Drives the rendering loop — add a new field here
 * and it appears in the form automatically.
 *
 * Each field: { name, label, type, required, icon, optional? }
 */
const FORM_FIELDS = [
    { name: "username",     label: "Username",                    type: "text",   required: true,  icon: <PersonIcon /> },
    { name: "firstname",    label: "First Name",                  type: "text",   required: true,  icon: <PersonIcon /> },
    { name: "lastname",     label: "Last Name",                   type: "text",   required: true,  icon: <PersonIcon /> },
    { name: "age",          label: "Age",                         type: "number", required: true,  icon: <CakeIcon /> },
    { name: "nationality",  label: "Nationality",                 type: "text",   required: true,  icon: <PublicIcon /> },
    { name: "email",        label: "Email Address",               type: "email",  required: true,  icon: <EmailIcon /> },
    { name: "phone",        label: "Phone Number",                type: "tel",    required: true,  icon: <PhoneIcon /> },
    { name: "organization", label: "Organization / School",       type: "text",   required: false, icon: <BusinessIcon />, optional: true },
];

// ============================================================
//  STYLED COMPONENTS
// ============================================================

/**
 * PageWrapper
 * Full-page white-tinted background with the Unsplash image.
 */
const PageWrapper = styled(Box)({
    minHeight: "100vh",
    paddingTop: "120px",
    paddingBottom: "60px",
    backgroundImage: `linear-gradient(rgba(255,255,255,0.88), rgba(255,255,255,0.88)), url(${BG_IMAGE})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    fontFamily: "'Poppins', sans-serif",
    px: 2,
});

/**
 * FormCard
 * Centered glassmorphism card containing the form.
 */
const FormCard = styled(Card)({
    width: "100%",
    maxWidth: 660,
    padding: "40px 36px",
    borderRadius: "20px",
    backgroundColor: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(0,0,0,0.08)",
    boxShadow: "0 8px 40px rgba(0,0,0,0.08)",
});

/**
 * SubmitButton
 * Dark branded submit button with gold text.
 */
const SubmitButton = styled(Button)({
    marginTop: "24px",
    backgroundColor: BRAND.dark,
    color: BRAND.gold,
    fontWeight: 700,
    fontSize: "1rem",
    padding: "13px",
    borderRadius: "10px",
    textTransform: "none",
    transition: "background-color 0.2s ease, transform 0.2s ease",
    "&:hover": {
        backgroundColor: "#1a0b3a",
        transform: "translateY(-1px)",
    },
    "&:disabled": {
        backgroundColor: "#ccc",
        color: "#888",
    },
});

// ============================================================
//  MAIN COMPONENT
// ============================================================

export default function PlanApplicationPage() {

    // ── Form State ───────────────────────────────────────────
    const [formData, setFormData]   = useState(INITIAL_FORM);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false); // loading state
    const [error, setError]         = useState("");

    // ============================================================
    //  HANDLERS
    // ============================================================

    /**
     * handleChange
     * Generic change handler for all text fields.
     * Uses the field's `name` attribute to update the right key.
     */
    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    /**
     * handleSubmit
     * Saves the form data to the Firestore "applications" collection.
     * Adds a server timestamp for admin reference.
     * Resets the form on success.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            await addDoc(collection(db, "applications"), {
                ...formData,
                submittedAt: serverTimestamp(), // Record when application was made
            });

            setSubmitted(true);
            setError("");
            setFormData(INITIAL_FORM); // Reset using the extracted constant

        } catch (err) {
            console.error("Error submitting application:", err);
            setError("Failed to submit application. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    // ============================================================
    //  RENDER
    // ============================================================

    return (
        <PageWrapper>
            <FormCard>

                {/* ── Form Header ── */}
                <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                    <Box sx={{
                        width: 48, height: 48, borderRadius: "12px",
                        backgroundColor: BRAND.dark,
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <SchoolIcon sx={{ color: BRAND.gold, fontSize: 26 }} />
                    </Box>
                    <Box>
                        <Typography
                            variant="h5" fontWeight={900}
                            sx={{ color: BRAND.dark, fontFamily: "'Georgia', serif", lineHeight: 1.2 }}
                        >
                            Apply for a Plan
                        </Typography>
                        <Typography variant="caption" sx={{ color: BRAND.muted }}>
                            ProDevConsult — Course Access Application
                        </Typography>
                    </Box>
                </Box>

                <Typography variant="body2" mb={3} sx={{ color: BRAND.muted, lineHeight: 1.7 }}>
                    Fill out the form below to register your interest. Once your payment
                    is confirmed, your administrator will grant you access to the Course Hub.
                </Typography>

                {/* Plan summary chips */}
                <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
                    {["16 Course Tracks", "Inbuilt Compiler", "DSA Training", "Certification"].map((f) => (
                        <Chip
                            key={f} label={f} size="small"
                            icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
                            sx={{
                                backgroundColor: "rgba(16,7,32,0.06)",
                                color: BRAND.dark,
                                fontWeight: 600,
                                fontSize: "0.72rem",
                                "& .MuiChip-icon": { color: BRAND.gold },
                            }}
                        />
                    ))}
                </Box>

                <Divider sx={{ mb: 3, borderColor: "rgba(0,0,0,0.07)" }} />

                {/* ── Form ── */}
                {/*
                    NOTE: We intentionally avoid using HTML <form> tags
                    for better compatibility with React's event system.
                    We use onSubmit on a Box with component="form" instead.
                */}
                <Box component="form" onSubmit={handleSubmit}>

                    {/* Text fields rendered from FORM_FIELDS array */}
                    {FORM_FIELDS.map((field) => (
                        <TextField
                            key={field.name}
                            label={field.label + (field.optional ? " (Optional)" : "")}
                            name={field.name}
                            type={field.type}
                            value={formData[field.name]}
                            onChange={handleChange}
                            fullWidth
                            margin="normal"
                            required={field.required}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Box sx={{ color: BRAND.gold, display: "flex" }}>
                                            {field.icon}
                                        </Box>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    ))}

                    {/* Plan selector */}
                    <TextField
                        select
                        label="Select Your Plan"
                        name="plan"
                        value={formData.plan}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        required
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SchoolIcon sx={{ color: BRAND.gold }} />
                                </InputAdornment>
                            ),
                        }}
                        helperText="Not sure? Visit our Pricing page to compare plans."
                    >
                        {PLAN_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                <Box>
                                    <Typography fontWeight={700} fontSize="0.9rem">
                                        {option.value}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.label.split("—")[1]?.trim()}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* Submit button */}
                    <SubmitButton
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={submitting}
                        startIcon={
                            submitting
                                ? <CircularProgress size={18} sx={{ color: BRAND.gold }} />
                                : <EditNoteIcon />
                        }
                    >
                        {submitting ? "Submitting..." : "Submit Application"}
                    </SubmitButton>

                </Box>

            </FormCard>

            {/* ── Success Snackbar ── */}
            <Snackbar
                open={submitted}
                autoHideDuration={5000}
                onClose={() => setSubmitted(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSubmitted(false)}
                    severity="success"
                    icon={<CheckCircleIcon />}
                    sx={{
                        width: "100%",
                        backgroundColor: "#10b981",
                        color: BRAND.white,
                        fontWeight: 600,
                    }}
                >
                    Application submitted! We'll contact you with your login credentials shortly.
                </Alert>
            </Snackbar>

            {/* ── Error Snackbar ── */}
            <Snackbar
                open={!!error}
                autoHideDuration={5000}
                onClose={() => setError("")}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setError("")}
                    severity="error"
                    sx={{
                        width: "100%",
                        backgroundColor: "#d32f2f",
                        color: BRAND.white,
                        fontWeight: 600,
                    }}
                >
                    {error}
                </Alert>
            </Snackbar>

        </PageWrapper>
    );
}
